import {
  Badge,
  Button,
  Card,
  CardHeader,
  Disclosure,
  EmptyState,
  FormField,
  Input,
  ListSkeleton,
  Markdown,
  PageHeader,
  Select,
} from '@nexus/ui';
import { queryOptions, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useModules } from '../modules/context';
import {
  cell,
  endpoints,
  fieldsOf,
  rulesOf,
  typeOf,
  type Endpoint,
  type OpenApiDocument,
} from './api-reference';
import { searchWords } from './guides';

export const OPENAPI_PATH = '/api-docs/openapi.json';

const openApiQuery = queryOptions({
  queryKey: ['api-docs'],
  queryFn: ({ signal }) => api.get<OpenApiDocument>(OPENAPI_PATH, { signal }),
  staleTime: 5 * 60_000,
});

const KNOWN_STATUS = ['200', '201', '204', '400', '401', '403', '404', '409', '429'] as const;

/** Details of one endpoint as Markdown: access, parameters, body and answers. */
function details({ operation }: Endpoint, t: TFunction): string {
  const rule = (schema: Parameters<typeof rulesOf>[0]) =>
    rulesOf(schema, {
      length: t('help.api.rules.length'),
      range: t('help.api.rules.range'),
      items: t('help.api.rules.items'),
      default: t('help.api.rules.default'),
    }).join(', ');
  const yes = (required: boolean) => (required ? t('help.api.yes') : t('help.api.no'));
  const lines: string[] = [];
  if (operation.description) lines.push(operation.description, '');

  const permission = operation['x-permission'];
  lines.push(
    operation['x-public']
      ? t('help.api.public')
      : permission
        ? t('help.api.permission', { permission: `\`${permission}\`` }) +
          (operation['x-scope'] ? ` ${t('help.api.scope', { scope: operation['x-scope'] })}` : '')
        : t('help.api.authenticated'),
    '',
  );

  if (operation.parameters.length) {
    lines.push(
      `**${t('help.api.parameters')}**`,
      '',
      `| ${t('help.api.columns.name')} | ${t('help.api.columns.in')} | ${t('help.api.columns.type')} | ${t('help.api.columns.required')} | ${t('help.api.columns.rules')} |`,
      '| --- | --- | --- | --- | --- |',
      ...operation.parameters.map(
        (p) =>
          `| \`${cell(p.name)}\` | ${t(`help.api.in.${p.in}`, { defaultValue: p.in })} | ${cell(typeOf(p.schema))} | ${yes(p.required)} | ${cell(rule(p.schema))} |`,
      ),
      '',
    );
  }

  const body = operation.requestBody?.content['application/json'].schema;
  if (body) {
    const fields = fieldsOf(body);
    lines.push(`**${t('help.api.body')}**`, '');
    if (fields.length) {
      lines.push(
        `| ${t('help.api.columns.field')} | ${t('help.api.columns.type')} | ${t('help.api.columns.required')} | ${t('help.api.columns.rules')} |`,
        '| --- | --- | --- | --- |',
        ...fields.map(
          (f) =>
            `| \`${cell(f.name)}\` | ${cell(typeOf(f.schema))} | ${yes(f.required)} | ${cell(rule(f.schema))} |`,
        ),
      );
    } else {
      lines.push(`\`${cell(typeOf(body))}\``);
    }
    lines.push('');
  }

  lines.push(
    `**${t('help.api.responses')}**`,
    '',
    ...Object.entries(operation.responses).map(([status, response]) => {
      const known = (KNOWN_STATUS as readonly string[]).includes(status);
      return `- \`${status}\` ${known ? t(`help.api.status.${status}`) : response.description}`;
    }),
  );
  return lines.join('\n');
}

const METHOD_TONE: Record<string, 'neutral' | 'brand' | 'accent'> = {
  get: 'neutral',
  delete: 'accent',
};

/** `/help/api`: every endpoint of the kernel and the active modules, from the OpenAPI document. */
export function ApiReferencePage() {
  const { t } = useTranslation();
  const modules = useModules();
  const doc = useQuery(openApiQuery);
  const [text, setText] = useState('');
  const [tag, setTag] = useState('all');

  const all = useMemo(() => (doc.data ? endpoints(doc.data) : []), [doc.data]);
  const label = (owner: string) => {
    if (owner === 'core') return t('help.api.kernel');
    const module = modules.find((m) => m.manifest.id === owner);
    return module ? t(module.manifest.name) : owner;
  };
  const words = searchWords(text);
  const shown = all.filter(
    (e) =>
      (tag === 'all' || e.operation.tags.includes(tag)) &&
      words.every((word) =>
        searchWords(
          `${e.method} ${e.path} ${e.operation.summary ?? ''} ${e.operation['x-permission'] ?? ''}`,
        )
          .join(' ')
          .includes(word),
      ),
  );
  const tags = [...new Set(all.flatMap((e) => e.operation.tags))];
  const groups = tags
    .map((owner) => ({ owner, items: shown.filter((e) => e.operation.tags[0] === owner) }))
    .filter((group) => group.items.length > 0);

  return (
    <>
      <PageHeader
        title={t('help.api.title')}
        description={t('help.api.description')}
        breadcrumbs={[
          { label: t('help.title'), href: '/help' },
          { label: t('help.areas.development') },
          { label: t('help.api.title') },
        ]}
        actions={
          <Button asChild variant="secondary" icon="download" iconOnlyOnPhone>
            <a href={`/api${OPENAPI_PATH}`} download="openapi.json">
              {t('help.api.download')}
            </a>
          </Button>
        }
      />
      <div className="flex flex-col gap-5">
        <Markdown source={t('help.api.intro')} />
        <div className="grid max-w-3xl gap-4 md:grid-cols-[1fr_16rem]">
          <FormField label={t('help.api.filter')}>
            {(field) => (
              <Input
                {...field}
                type="search"
                autoComplete="off"
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('help.area')}>
            {(field) => (
              <Select
                id={field.id}
                value={tag}
                onValueChange={setTag}
                options={[
                  { value: 'all', label: t('help.allAreas') },
                  ...tags.map((value) => ({ value, label: label(value) })),
                ]}
              />
            )}
          </FormField>
        </div>
        {doc.isPending ? (
          <ListSkeleton rows={6} title={false} trailing />
        ) : doc.isError ? (
          <EmptyState icon="cloud-off" title={t('common.loadFailed')} />
        ) : groups.length === 0 ? (
          <EmptyState
            icon="search"
            title={t('help.emptyTitle')}
            description={t('help.api.empty')}
          />
        ) : (
          groups.map((group) => (
            <Card key={group.owner}>
              <CardHeader
                title={label(group.owner)}
                description={t('help.api.count', { count: group.items.length })}
              />
              <div className="flex flex-col p-1 pb-2">
                {group.items.map((endpoint) => (
                  <Disclosure
                    key={`${endpoint.method} ${endpoint.path}`}
                    summary={
                      <>
                        <Badge
                          tone={METHOD_TONE[endpoint.method] ?? 'brand'}
                          className="w-16 justify-center font-mono"
                        >
                          {endpoint.method.toUpperCase()}
                        </Badge>
                        <code className="min-w-0 font-mono text-sm break-all text-fg">
                          {endpoint.path}
                        </code>
                        <span className="text-fg-muted">{endpoint.operation.summary}</span>
                      </>
                    }
                  >
                    <Markdown source={details(endpoint, t)} className="text-sm" />
                  </Disclosure>
                ))}
              </div>
            </Card>
          ))
        )}
      </div>
    </>
  );
}
